/**
 * TasksWidget — Tuile « Mes tâches » du dashboard.
 *
 * - Liste des 5 prochaines tâches non terminées (triées par échéance puis priorité)
 * - Ajout rapide inline (titre + date/heure + priorité)
 * - Toggle done direct, suppression, ouverture modale d'édition
 * - Compteurs : total ouvertes, en retard, aujourd'hui
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ListChecks, Plus, Calendar, Trash2, Pencil, X, AlertCircle, Clock,
} from 'lucide-react';
import api from '../api/client';

const PRIORITY_LABEL = {
  urgent: 'Urgent',
  high: 'Haute',
  normal: 'Normale',
  low: 'Basse',
};
const PRIORITY_BADGE = {
  urgent: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  normal: 'bg-slate-100 text-slate-600 border-slate-200',
  low: 'bg-slate-50 text-slate-500 border-slate-100',
};

function fmtDueShort(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Auj. · ${time}`;
  const day = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  return `${day} · ${time}`;
}

function isOverdue(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getTime() < Date.now();
}

// ---- Quick add form ----------------------------------------------------------
function QuickAdd({ onCreated }) {
  const [title, setTitle] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [priority, setPriority] = useState('normal');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const payload = { title: title.trim(), priority };
      if (dueAt) payload.due_at = new Date(dueAt).toISOString();
      const { data } = await api.post('/tasks', payload);
      onCreated(data);
      setTitle('');
      setDueAt('');
      setPriority('normal');
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-1.5 flex-shrink-0 border-b border-slate-100 pb-2 mb-2">
      <input
        type="text"
        data-testid="task-quick-title"
        placeholder="Nouvelle tâche…"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full text-[12px] rounded-md border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-300"
      />
      <div className="flex items-center gap-1.5">
        <input
          type="datetime-local"
          data-testid="task-quick-due"
          value={dueAt}
          onChange={(e) => setDueAt(e.target.value)}
          className="flex-1 text-[11px] rounded-md border border-slate-200 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
        <select
          value={priority}
          data-testid="task-quick-priority"
          onChange={(e) => setPriority(e.target.value)}
          className="text-[11px] rounded-md border border-slate-200 px-2 py-1 bg-white"
        >
          <option value="low">Basse</option>
          <option value="normal">Normale</option>
          <option value="high">Haute</option>
          <option value="urgent">Urgent</option>
        </select>
        <button
          type="submit"
          disabled={!title.trim() || saving}
          data-testid="task-quick-submit"
          className="inline-flex items-center gap-0.5 rounded-md bg-[#4F46E5] hover:bg-[#4338CA] disabled:opacity-50 text-white px-2 py-1 text-[11px] font-semibold"
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </form>
  );
}

// ---- One task row ------------------------------------------------------------
function TaskRow({ task, onToggle, onDelete, onEdit }) {
  const overdue = !task.done && isOverdue(task.due_at);
  const dueLabel = fmtDueShort(task.due_at);
  return (
    <div
      data-testid={`task-row-${task.id}`}
      className={`group flex items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-slate-50 ${task.done ? 'opacity-60' : ''}`}
    >
      <input
        type="checkbox"
        checked={!!task.done}
        onChange={() => onToggle(task)}
        data-testid={`task-toggle-${task.id}`}
        className="w-3.5 h-3.5 accent-indigo-600 cursor-pointer flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className={`text-[12px] font-medium text-slate-900 truncate ${task.done ? 'line-through' : ''}`}>
          {task.title}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {dueLabel && (
            <span className={`inline-flex items-center gap-0.5 text-[10px] ${overdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
              {overdue ? <AlertCircle className="w-2.5 h-2.5" /> : <Clock className="w-2.5 h-2.5" />}
              {dueLabel}
            </span>
          )}
          {task.priority !== 'normal' && (
            <span className={`text-[9.5px] font-semibold border rounded px-1 py-px ${PRIORITY_BADGE[task.priority]}`}>
              {PRIORITY_LABEL[task.priority]}
            </span>
          )}
        </div>
      </div>
      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
        <button
          type="button"
          onClick={() => onEdit(task)}
          data-testid={`task-edit-${task.id}`}
          title="Modifier"
          className="p-1 rounded hover:bg-slate-200 text-slate-500"
        >
          <Pencil className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(task)}
          data-testid={`task-delete-${task.id}`}
          title="Supprimer"
          className="p-1 rounded hover:bg-red-100 text-red-500"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ---- Edit modal --------------------------------------------------------------
function EditModal({ task, onClose, onSaved }) {
  const [title, setTitle] = useState(task.title || '');
  const [description, setDescription] = useState(task.description || '');
  const [dueAt, setDueAt] = useState(task.due_at ? task.due_at.slice(0, 16) : '');
  const [priority, setPriority] = useState(task.priority || 'normal');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        priority,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
      };
      const { data } = await api.patch(`/tasks/${task.id}`, payload);
      onSaved(data);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="task-edit-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-[15px] font-semibold text-slate-900">Modifier la tâche</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Titre</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="task-edit-title"
              className="w-full text-[13px] rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Description</label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="task-edit-description"
              className="w-full text-[12.5px] rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Échéance</label>
              <input
                type="datetime-local"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                data-testid="task-edit-due"
                className="w-full text-[12px] rounded-md border border-slate-200 px-2 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1 block">Priorité</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                data-testid="task-edit-priority"
                className="w-full text-[12px] rounded-md border border-slate-200 px-2 py-2 bg-white"
              >
                <option value="low">Basse</option>
                <option value="normal">Normale</option>
                <option value="high">Haute</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[12.5px] rounded-md px-3 py-1.5 border border-slate-200 text-slate-700 hover:bg-slate-50">
            Annuler
          </button>
          <button
            onClick={save}
            disabled={saving || !title.trim()}
            data-testid="task-edit-save"
            className="text-[12.5px] rounded-md px-3 py-1.5 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-semibold disabled:opacity-50"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- MAIN widget -------------------------------------------------------------
export default function TasksWidget() {
  const [data, setData] = useState({ next: [], counters: { total_open: 0, overdue: 0, today: 0 } });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    try {
      const { data: d } = await api.get('/tasks/widget');
      setData(d);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onToggle = async (task) => {
    try {
      await api.patch(`/tasks/${task.id}`, { done: !task.done });
      refresh();
    } catch { /* silent */ }
  };
  const onDelete = async (task) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      refresh();
    } catch { /* silent */ }
  };

  const counters = data.counters || { total_open: 0, overdue: 0, today: 0 };

  return (
    <div
      data-testid="dashboard-tasks-widget"
      className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden"
    >
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <ListChecks className="w-3.5 h-3.5 text-indigo-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Mes tâches</h3>
        </div>
        <div className="flex items-center gap-1 text-[10px] font-semibold">
          {counters.overdue > 0 && (
            <span data-testid="task-counter-overdue" className="rounded bg-red-50 text-red-700 px-1.5 py-0.5 border border-red-100">
              {counters.overdue} en retard
            </span>
          )}
          {counters.today > 0 && (
            <span data-testid="task-counter-today" className="rounded bg-amber-50 text-amber-700 px-1.5 py-0.5 border border-amber-100">
              {counters.today} auj.
            </span>
          )}
          <span data-testid="task-counter-total" className="rounded bg-slate-100 text-slate-600 px-1.5 py-0.5">
            {counters.total_open} ouverte{counters.total_open > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <QuickAdd onCreated={() => refresh()} />

      <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1 space-y-0.5">
        {loading && (
          <div className="text-[11px] text-slate-400 text-center py-4">Chargement…</div>
        )}
        {!loading && data.next.length === 0 && (
          <div className="flex-1 w-full flex flex-col items-center justify-center text-center text-[11px] text-slate-400 italic py-4">
            Aucune tâche pour le moment. Ajoutez-en une !
          </div>
        )}
        {!loading && data.next.map((t) => (
          <TaskRow
            key={t.id}
            task={t}
            onToggle={onToggle}
            onDelete={onDelete}
            onEdit={(task) => setEditing(task)}
          />
        ))}
      </div>

      {editing && (
        <EditModal
          task={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { refresh(); }}
        />
      )}
    </div>
  );
}
